import { z } from 'zod';
import {
  promotionGestureSchema,
  promotionLineStatusSchema,
  syncRefusalReasonSchema,
} from './sync.js';
import { versionPinSchema, versionedKindSchema } from './versioning.js';

/**
 * The OPERATOR side of the passage to production (vitrine.md section 4, lot D of
 * issue #192): what the box of the cascade reads and writes on the ATELIER.
 *
 * `sync.ts` is the machine contract between the two servers; this is the one
 * between the atelier and its own screen. The box asks the atelier to JUDGE a
 * gesture (`check`, changes nothing anywhere) and then to PASS it (`apply`): the
 * atelier computes the cascade, has the vitrine judge it, and - only when the
 * vitrine applied - writes its marks and pointers. Both answer the same shape,
 * so the box is drawn by one function whatever it asked.
 */

export const PROMOTION_ROUTES = {
  /** POST a `PromotionBoxRequest`, answers a `PromotionBox`, changes nothing (§4.3). */
  check: '/api/atelier/promotion/check',
  /** POST a `PromotionBoxRequest`, answers a `PromotionBox`, passes the cascade for good (§4.4). */
  apply: '/api/atelier/promotion/apply',
} as const;

/** The body of both routes: the gesture, and nothing else - the atelier computes the cascade. */
export const promotionBoxRequestSchema = z.object({ gesture: promotionGestureSchema }).strict();
export type PromotionBoxRequest = z.infer<typeof promotionBoxRequestSchema>;

/** One version, named for a sentence: « Sens profond v4 », « cité par ... ». */
export const promotionCitationSchema = z
  .object({
    kind: versionedKindSchema,
    entityId: z.string().min(1),
    name: z.string().min(1),
    number: z.number().int().positive(),
  })
  .strict();
export type PromotionCitation = z.infer<typeof promotionCitationSchema>;

/**
 * One version of the cascade, as the box lists it (§4.3): what goes, how it is
 * cited, what the prod already holds, and who else is touched.
 *
 * `status` is the verdict of the vitrine for a version that travels, `already`
 * for one the vitrine holds already (the atelier knows it by its mark), and
 * `refused` with its `reason` for what the vitrine - or the atelier, before
 * calling it - cannot take.
 */
export const promotionBoxLineSchema = z
  .object({
    kind: versionedKindSchema,
    entityId: z.string().min(1),
    entityName: z.string().min(1),
    versionNumber: z.number().int().positive(),
    /** The gesture's own version: the one the operator clicked on. */
    root: z.boolean(),
    /** How the cascade cites it: `latest`, or the version a referent pins. Empty for the root. */
    pins: z.array(versionPinSchema),
    /** Who, in the cascade, cites it. Empty for the root. */
    citedBy: z.array(promotionCitationSchema),
    status: promotionLineStatusSchema,
    reason: syncRefusalReasonSchema.optional(),
    /** The version « latest prod » sits on today, or null when the entity was never passed. */
    prodNumber: z.number().int().positive().nullable(),
    /** Whether « latest prod » moves onto this version once the cascade is applied. */
    advancesPointer: z.boolean(),
    /**
     * The other referents the prod plays by `latest` that this move changes (§4.2):
     * « aussi en production via latest : Le Mur v4 ». Empty when no pointer moves.
     */
    alsoViaLatest: z.array(promotionCitationSchema),
  })
  .strict();
export type PromotionBoxLine = z.infer<typeof promotionBoxLineSchema>;

/** One media file the cascade cites (§4.2): the library is not versioned, so it has no number. */
export const promotionBoxAssetSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    status: promotionLineStatusSchema,
    reason: syncRefusalReasonSchema.optional(),
    /** The version of the cascade that cites it. */
    citedBy: z.array(promotionCitationSchema),
  })
  .strict();
export type PromotionBoxAsset = z.infer<typeof promotionBoxAssetSchema>;

/**
 * Where the box stands (§4.3, §12):
 *
 * - `ok`: the vitrine takes everything - the button says how many elements go;
 * - `refused`: something is refused, a line or the gesture names it - the button
 *   is inert and carries the reason;
 * - `unreachable`: the vitrine did not answer, nothing changed anywhere - « Réessayer »;
 * - `local`: no vitrine is linked (`PROD_ORIGIN` unset), the passage stays on this
 *   server - the old confirmation, with its sentence.
 *
 * On an `apply`, `applied` says whether it went through; the state then stays the
 * one the judgment ended on.
 */
export const promotionBoxStateSchema = z.enum(['ok', 'refused', 'unreachable', 'local']);
export type PromotionBoxState = z.infer<typeof promotionBoxStateSchema>;

export const promotionBoxSchema = z
  .object({
    gesture: promotionGestureSchema,
    configured: z.boolean(),
    state: promotionBoxStateSchema,
    applied: z.boolean(),
    /** The code of the vitrine that judged (`GET /api/instance`), null when it did not. */
    prodCode: z.string().nullable(),
    /** In dependency order, leaves first, the root last. Empty for a retirement or a rename. */
    lines: z.array(promotionBoxLineSchema),
    assets: z.array(promotionBoxAssetSchema),
    /** The refusal of the gesture itself, when it is not on a line. */
    gestureRefusal: syncRefusalReasonSchema.optional(),
    /** The sentence for `unreachable`, in French. */
    message: z.string().nullable(),
  })
  .strict();
export type PromotionBox = z.infer<typeof promotionBoxSchema>;

/**
 * What the box says in its summary line (§4.3): « 7 éléments partent, 1 refus ».
 * Elements are the versions and the files that travel; a version the vitrine
 * holds already is not counted - it does not leave.
 */
export function promotionCounts(box: Pick<PromotionBox, 'lines' | 'assets'>): {
  sends: number;
  already: number;
  refused: number;
} {
  const all = [...box.lines, ...box.assets];
  return {
    sends: all.filter((item) => item.status === 'sends').length,
    already: all.filter((item) => item.status === 'already').length,
    refused: all.filter((item) => item.status === 'refused').length,
  };
}
